import {
  addDays,
  areRangesOverlapping,
  differenceInDays,
  eachDay,
  endOfDay,
  endOfWeek,
  format,
  isAfter,
  isThisWeek,
  startOfWeek
} from 'date-fns';
import { Day, IMediaItemDuration, IViewMode, IViewModeColumn, MediaItem, TimelineDataItem } from '../store';
import { TreeItem, ViewModeType } from '../store/types';
import * as Utils from './utils';

export class WeeksViewMode implements IViewMode {
  public type = ViewModeType.Weeks;
  public columns: IViewModeColumn[];
  constructor() {
    this.computeColumns = this.computeColumns.bind(this);
  }

  public computeColumns = (startDate: Date, endDate: Date) => {
    this.columns = [];
    const width = 175;
    startDate = startOfWeek(startDate);
    endDate = endOfDay(endDate);
    let currDate = startDate;
    while (!isAfter(currDate, endDate)) {
      const s = startOfWeek(currDate);
      const e = endOfWeek(currDate);
      this.columns.push({
        startDate: s,
        endDate: e,
        title: `${format(s, 'DD.MM')} - ${format(e, 'DD.MM')}`,
        subtitle: `${format(s, 'MMMM YYYY')}`,
        width,
        ...this.isColumnNow(currDate, width),
        headerCellClassModifier: '',
        headerTitleClassModifier: 'weeks',
        headerSubtitleClassModifier: 'weeks'
      });
      currDate = addDays(currDate, 7);
    }
  };

  public applyToMedia = (media: MediaItem, treeItem: TreeItem): TimelineDataItem[] => {
    // if the media has no days allocated then display nothing
    if (media.diffusionDays === Day.None) {
      return [];
    }

    // check the days in which the media is playing
    const daysInPlay = Utils.daysForDiffusionFlag(media.diffusionDays);
    const tDaysInPlay = Utils.daysForDiffusionFlag(treeItem.diffusionDays);
    if (daysInPlay.length === 0) {
      return [];
    }

    // compute the range of the timeline bars in normalized coordinates for each column
    // this view mode can have multiple bars per column(some medias may be set to not play in certain days of a week)
    return this.columns.map((c, i) => {
      // check if the media range and the column range overlap. if not, return no bars
      if (areRangesOverlapping(c.startDate, c.endDate, media.startDate, media.endDate)) {
        return this.GetDurationBarsForColumn(
          c.width,
          i,
          media.mediaType,
          daysInPlay,
          tDaysInPlay,
          media.startDate,
          media.endDate,
          treeItem.startDate,
          treeItem.endDate,
          c.startDate,
          c.endDate
        );
      }
      // by default we return no durations for the column
      return {
        colIndex: i,
        mediaType: media.mediaType,
        durations: [],
        insideValidRange: true
      };
    });
  };

  private GetDurationBarsForColumn(
    colWidth: number,
    colIndex: number,
    mediaType: number,
    daysInPlay: number[],
    tDaysInPlay: number[],
    mStartDate: Date,
    mEndDate: Date,
    tStartDate: Date | null,
    tEndDate: Date | null,
    colStartDate: Date,
    colEndDate: Date
  ): TimelineDataItem {
    // get all the days in the column range
    const daysInColumn = eachDay(colStartDate, colEndDate);
    // go through the days and build the intervals
    let currentInterval: IMediaItemDuration | null = null;
    const intervals: IMediaItemDuration[] = [];

    daysInColumn.forEach((day, index) => {
      // check if the day is valid - the media is in play in that day of the week and the day is inside the media range
      if (daysInPlay.indexOf(day.getDay()) !== -1 && (day >= mStartDate && day <= mEndDate)) {
        let dayInValidRange = false;
        if (tStartDate && tEndDate) {
          dayInValidRange = day >= tStartDate && day <= tEndDate && tDaysInPlay.indexOf(day.getDay()) !== -1;
        }

        if (currentInterval === null) {
          currentInterval = { start: index, end: -1, insideValidRange: dayInValidRange };
          intervals.push(currentInterval);
        } else if (currentInterval.insideValidRange !== dayInValidRange) {
          currentInterval.end = index;
          currentInterval = { start: index, end: -1, insideValidRange: dayInValidRange };
          intervals.push(currentInterval);
        }
      } else if (currentInterval !== null) {
        currentInterval.end = index;
        currentInterval = null;
      }
    });

    if (intervals.length > 0) {
      // close the last interval
      if (intervals[intervals.length - 1].end === -1) {
        intervals[intervals.length - 1].end = 7;
      }
    }

    const durations: IMediaItemDuration[] = intervals.map(i => ({
      start: i.start / 7,
      end: i.end / 7,
      insideValidRange: i.insideValidRange
    }));
    return {
      colIndex,
      mediaType,
      durations
    };
  }

  private isColumnNow = (date: Date, width: number) => {
    const includesNow = isThisWeek(date);
    if (!includesNow) {
      return { includesNow, nowPosition: -1 };
    }
    const beginningOfThisWeek = startOfWeek(date);
    const now = new Date();
    const nowPosition = (differenceInDays(now, beginningOfThisWeek) / 7) * width; // 1440 - number of minutes in a day
    return { includesNow, nowPosition };
  };
}



// WEBPACK FOOTER //
// ./src/viewModes/WeeksViewMode.ts